import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { ViewColumn } from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import {
    DataGridPro,
    GridFilterModel,
    GridFilterPanel,
    GridRow,
    GridRowId,
    GridRowProps,
    GridRowSelectionModel,
    useGridApiRef,
} from "@mui/x-data-grid-pro";
import { isFunction } from "lodash";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isStringType, isType } from "utils/TypeGuards";
import AcxMenu, { AcxMenuItemProps, StyledMenuLabel } from "../Menu/AcxMenu";
import AcxDataGridStore, { CustomControlItem } from "./AcxDataGridStore";
import AcxGridSelection from "./AcxGridSelection";
import FilterIcon from "./FilterOverride/FilterIcon";
import HideColumn from "./HideColumnOverride/HideColumn";
import VertIcon from "./VertIcon";
import theme from "Theme/AppTheme";
import AcxEmptyState from "../AcxEmptyStateIndicator/AcxEmptyState";

interface IAcxDataGrid {
    dataGridStore: AcxDataGridStore;
    disableColumnResize?: boolean;
    /**
     * Renders for an image capture: every row on the current page at its natural
     * height, with no virtualization, header controls or footer.
     */
    exportCapture?: boolean;
}

const AcxDataGrid = observer((props: React.PropsWithChildren<IAcxDataGrid>) => {
    const navigate = useNavigate();

    const gridApi = useGridApiRef();

    const store = props.dataGridStore;

    const filterIconRef = React.useRef<HTMLDivElement>(null);

    const handleVertClick = (event: React.MouseEvent<HTMLElement>) => {
        store.menuAnchor = event.currentTarget;
        event.stopPropagation();
        event.preventDefault();
    };

    const handleVertMenuClose = () => {
        store.menuAnchor = null;
    };

    const onClear = () => {
        if (store.isSelectionReadOnly) return;
        store.clearSelected();
    };

    React.useEffect(() => {
        if (store?.gridApi?.current) {
            if (!store.disableLocalConfig) {
                store.gridApi.current.subscribeEvent("columnResizeStop", () => {
                    store.onColResizeOrReorder(
                        store.gridApi?.current.getAllColumns() ?? [],
                    );
                });
                store.gridApi.current.subscribeEvent(
                    "columnOrderChange",
                    () => {
                        store.onColResizeOrReorder(
                            store.gridApi?.current.getAllColumns() ?? [],
                        );
                    },
                );
            }

            store.gridApi.current.subscribeEvent(
                "rowCountChange",
                (count: number) => {
                    props.dataGridStore?.onRowCountChange?.(count);
                },
            );
        }
    }, [store.gridApi, store, props.dataGridStore]);

    function isTypeElementAndSize(
        arg: React.ReactElement | CustomControlItem,
    ): arg is CustomControlItem {
        return isType<CustomControlItem>(arg, "controlElement");
    }

    const getHeader = () => {
        if (store.title) {
            return <Typography variant="subtitle2">{store.title}</Typography>;
        }

        if (isStringType(store.rowTerm)) {
            const header = `${store.rows?.length} ${store.rowTerm ?? "Items"} `;
            return (
                <Typography variant="subtitle1">
                    {header}
                    {store.selectedRowIds &&
                        store.selectedRowIds.length > 0 &&
                        !store.selectionContextOptions && (
                            <>
                                ({store.selectedRowIds.length} selected
                                <Tooltip title="Clear all selections">
                                    <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={onClear}
                                        disabled={store.isSelectionReadOnly}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                )
                            </>
                        )}
                </Typography>
            );
        } else {
            return store.rowTerm;
        }
    };

    const headerColumnSpan = store.headerColumnSpan
        ? store.headerColumnSpan
        : 3;

    const controlsColumnSpan = store.controlsColumnSpan
        ? store.controlsColumnSpan
        : 9;

    useEffect(() => {
        const hideColumnMenuItem = {
            props: {
                onClick: () => {
                    store.closeMenu();
                    store.showHideColumns();
                },
            },
            icon: <ViewColumn />,
            id: `${store.gridId}-hide-col-menu-item`,
            label: <StyledMenuLabel>Manage Columns</StyledMenuLabel>,
        } as AcxMenuItemProps;
        store.vertIconMenuItems = isFunction(store.vertIconMenuItemsBuilder)
            ? store.vertIconMenuItemsBuilder(store.closeMenu, navigate)
            : store.vertIconMenuItemsBuilder ?? [];

        store.vertIconMenuItems.push(hideColumnMenuItem);
    }, [navigate, store, store.vertIconMenuItemsBuilder]);

    useEffect(() => {
        props.dataGridStore.gridApi = gridApi;
    }, [gridApi, props.dataGridStore]);

    const styledColumns = useCallback(() => {
        return store.columns.map((column, index) => {
            return {
                ...column,
                ...(store.columns.length - 1 && {
                    headerClassName: "column-header-end",
                }),
            };
        });
    }, [store.columns]);

    // Row slot identity has to stay stable or every render remounts the rows.
    const rowSlot = useCallback(
        (rowProps: GridRowProps) => {
            const tooltip = store.getRowTooltip?.(rowProps.row);
            if (!tooltip) return <GridRow {...rowProps} />;

            return (
                <Tooltip title={tooltip} followCursor>
                    <GridRow {...rowProps} />
                </Tooltip>
            );
        },
        [store],
    );

    return (
        <>
            {!store.hideHeader && !props.exportCapture && (
                <Grid
                    container
                    alignItems="flex-end"
                    sx={{
                        position: "relative",
                        marginRight: "-5px",
                        marginTop: theme.spacing(1.5),
                        marginBottom: theme.spacing(0.5),
                        maxHeight: "max-content",
                    }}
                >
                    {Boolean(store.selectionContextOptions) && (
                        <Grid item xs={12} style={{ marginBottom: "16px" }}>
                            <AcxGridSelection store={store} />
                        </Grid>
                    )}

                    <Grid
                        container
                        item
                        xs={headerColumnSpan}
                        justifyContent={store.headersJustifyProperty}
                    >
                        {getHeader()}
                    </Grid>

                    <Grid item xs={controlsColumnSpan}>
                        <Grid
                            container
                            justifyContent={
                                store.controlsJustifyProperty ?? "flex-end"
                            }
                            alignItems={
                                store.controlsAlignProperty ?? "flex-end"
                            }
                            alignContent="flex-end"
                            style={{
                                ...store.controlsColumnStyle,
                                marginLeft: "0px",
                            }}
                        >
                            {props.children && (
                                <Grid
                                    container
                                    item
                                    sx={(theme) =>
                                        store.controlMargin
                                            ? store.controlMargin
                                            : {
                                                  marginBottom:
                                                      theme.spacing(1),
                                                  marginRight: theme.spacing(1),
                                              }
                                    }
                                    flexWrap="nowrap"
                                    columnGap={1}
                                    width="fit-content"
                                    alignItems="center"
                                >
                                    {props.children}
                                </Grid>
                            )}
                            {store.controls?.map((arg, index) => {
                                let control = arg;

                                if (isTypeElementAndSize(arg)) {
                                    control = arg.controlElement;
                                }

                                const controlMargin = store.controlMargin ?? {
                                    marginRight: "0.5rem",
                                    marginBottom: "0.5rem",
                                };

                                return (
                                    <span
                                        key={
                                            (control as React.ReactElement)
                                                .key ?? "control-" + index
                                        }
                                        style={{
                                            ...controlMargin,
                                            ...(arg as CustomControlItem).style,
                                        }}
                                    >
                                        {control as React.ReactNode}
                                    </span>
                                );
                            })}
                            {!store.hideFilter && (
                                <Grid
                                    container
                                    item
                                    sx={(theme) =>
                                        store.controlMargin
                                            ? store.controlMargin
                                            : {
                                                  marginBottom:
                                                      theme.spacing(1),
                                              }
                                    }
                                    flexWrap="nowrap"
                                    columnGap={1}
                                    width="fit-content"
                                    alignItems="center"
                                >
                                    <FilterIcon
                                        store={store}
                                        // @ts-ignore
                                        onClick={(e) => {
                                            store.gridApi?.current?.showFilterPanel();
                                        }}
                                        vertIconEnabled={
                                            !!store.vertIconMenuItems
                                        }
                                        ref={filterIconRef}
                                        gridApiRef={store.gridApi}
                                        isFiltered={
                                            !!store.gridApi?.current?.state
                                                ?.filter?.filterModel?.items
                                                .length &&
                                            store.gridApi?.current?.state?.filter?.filterModel?.items.every(
                                                (item) => !!item.value,
                                            )
                                        }
                                    />
                                </Grid>
                            )}
                            {store.vertIconMenuItems &&
                                Array.isArray(store.vertIconMenuItems) &&
                                store.vertIconMenuItems.length > 0 &&
                                !store.hideVertIcon && (
                                    <Grid
                                        container
                                        item
                                        sx={(theme) =>
                                            store.controlMargin
                                                ? store.controlMargin
                                                : {
                                                      marginBottom:
                                                          theme.spacing(1),
                                                      marginRight:
                                                          theme.spacing(1),
                                                  }
                                        }
                                        flexWrap="nowrap"
                                        columnGap={1}
                                        width="fit-content"
                                        alignItems="center"
                                    >
                                        <Grid item>
                                            <VertIcon
                                                onClick={handleVertClick}
                                            />

                                            <AcxMenu
                                                menuItems={
                                                    isFunction(
                                                        store.vertIconMenuItems,
                                                    )
                                                        ? store.vertIconMenuItems(
                                                              store.closeMenu,
                                                              navigate,
                                                          )
                                                        : store.vertIconMenuItems
                                                }
                                                anchorElement={store.menuAnchor}
                                                onMenuClose={
                                                    handleVertMenuClose
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {store.preHeader && !props.exportCapture && (
                <Grid item>{store.preHeader}</Grid>
            )}
            <div
                style={{
                    width: "100%",
                    height: props.exportCapture
                        ? "auto"
                        : store.heightOverride
                          ? store.heightOverride
                          : `calc(100% - ${store.removeHeight ?? "0px"})`,
                    marginTop: "0.25rem",
                    boxShadow:
                        "0 0 3px 0 rgba(0, 0, 0, 0.05), 0 1px 1px 0 rgba(0, 0, 0, 0.05)",
                    overscrollBehaviorX: "contain",
                    overscrollBehaviorY: "auto",
                    backgroundColor: theme.palette.white.main,
                    borderRadius: theme.shape.borderRadius * 2,
                }}
            >
                <DataGridPro
                    className="pendo-ignore"
                    columnVisibilityModel={store.visibilityModel}
                    columns={styledColumns()}
                    rows={toJS(store.rows)}
                    pinnedRows={
                        store.pinnedBottomRows
                            ? { bottom: toJS(store.pinnedBottomRows) }
                            : undefined
                    }
                    // Keep selection controlled by the store so checkboxes stay in sync
                    // when rows refresh; also keep selections across pagination/filtering.
                    rowSelectionModel={toJS(store.selectedRowIds)}
                    keepNonExistentRowsSelected
                    checkboxSelection={store.checkboxSelection}
                    loading={store.isLoading}
                    apiRef={store.gridApi}
                    isRowSelectable={store.isRowSelectable}
                    columnHeaderHeight={52}
                    rowHeight={42}
                    slots={{
                        ...(store.getRowTooltip ? { row: rowSlot } : {}),
                        filterPanel: GridFilterPanel,
                        noRowsOverlay: () => (
                            <Grid
                                container
                                justifyContent={"center"}
                                height={"100%"}
                            >
                                <AcxEmptyState size="small" variant="empty" />
                            </Grid>
                        ),
                        loadingOverlay: () => (
                            <Grid
                                container
                                justifyContent={"center"}
                                height={"100%"}
                                style={{
                                    backgroundColor: theme.palette.white.main,
                                }}
                            >
                                <AcxEmptyState size="small" variant="loading" />
                            </Grid>
                        ),
                    }}
                    onCellClick={store.onCellClick}
                    processRowUpdate={store.processRowUpdate}
                    onProcessRowUpdateError={store.onProcessRowUpdateError}
                    slotProps={{
                        ...(store.isSelectionReadOnly
                            ? {
                                  baseCheckbox: {
                                      disabled: true,
                                  },
                              }
                            : {}),
                        filterPanel: {
                            filterFormProps: {
                                logicOperatorInputProps: {
                                    variant: "outlined",
                                    size: "small",
                                    sx: {
                                        mr: theme.spacing(0.5),
                                        mt: theme.spacing(0.5),
                                    },
                                },
                                columnInputProps: {
                                    variant: "outlined",
                                    size: "small",
                                    sx: {
                                        mr: theme.spacing(0.5),
                                        mt: theme.spacing(0.5),
                                    },
                                    label: "Select Field",
                                },
                                operatorInputProps: {
                                    variant: "outlined",
                                    size: "small",
                                    sx: {
                                        mr: theme.spacing(0.5),
                                        mt: theme.spacing(0.5),
                                    },
                                    // TODO: figure out how to change this label... why is it different from valueInputProps?
                                    // InputComponentProps: {
                                    //     label: "Select Operator",
                                    // },
                                    // label: "Select Operator",
                                    // placeholder: "test",
                                    // inputLabel: "test",
                                    // formLabel: "test",
                                    // legend: "test",
                                },
                                valueInputProps: {
                                    InputComponentProps: {
                                        variant: "outlined",
                                        size: "small",
                                        sx: {
                                            mr: theme.spacing(0.5),
                                            mt: theme.spacing(0.5),
                                        },
                                        label: "Search Value",
                                    },
                                },
                            },
                        },
                    }}
                    disableColumnMenu
                    pagination={store.pagination}
                    pageSizeOptions={store.pageSizeOptions}
                    rowCount={store.rowCount ?? store.rows.length}
                    paginationModel={
                        store.paginationModel ?? { pageSize: 10, page: 0 }
                    }
                    onPaginationModelChange={store.onPaginationModelChange}
                    paginationMode={store.paginationMode}
                    onRowSelectionModelChange={(
                        rowSelectionModel: GridRowSelectionModel,
                    ) => {
                        if (store.isSelectionReadOnly) return;
                        props.dataGridStore.handleSelectionChange(
                            rowSelectionModel as GridRowId[],
                        );
                    }}
                    filterModel={store.filterModel}
                    onFilterModelChange={(input: GridFilterModel) => {
                        if (!props.dataGridStore) return;
                        props.dataGridStore.filterModel = input;
                    }}
                    sortingMode={store.sortingMode}
                    sortModel={store.sortModel}
                    onSortModelChange={store.setSortModel}
                    disableRowSelectionOnClick={
                        store.disableRowSelectionOnClick
                    }
                    getRowHeight={
                        store.rowHeightOverride ? () => "auto" : undefined
                    }
                    hideFooterPagination={store.hideFooterPagination}
                    hideFooter={props.exportCapture}
                    autoHeight={props.exportCapture}
                    disableVirtualization={props.exportCapture}
                    sx={{
                        "--unstable_DataGrid-radius":
                            theme.shape.borderRadius * 2 + "px",
                        "--DataGrid-containerBackground":
                            theme.palette.gray[50] || "#F9FAFB",
                        "--DataGrid-rowBorderColor":
                            theme.palette.gray[200] || "#E5E7EB",
                        ".clickable-cell": {
                            padding: 0,
                        },
                        ".column-header-end": {
                            borderTopRightRadius:
                                "calc(var(--unstable_DataGrid-radius) - 1px)",
                        },
                        ".no-padding": {
                            padding: 0,
                        },
                        ".MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row": {
                            backgroundColor:
                                theme.palette.gray[50] || "#F9FAFB",
                        },

                        "MuiDataGrid-footerContainer": {
                            borderColor: theme.palette.gray[200],
                        },
                        ".MuiDataGrid-columnHeader": {
                            padding: 0,
                        },
                        ".MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitleContainer":
                            {
                                justifyContent: "start",
                            },
                        ".MuiDataGrid-columnHeaderTitle": {
                            fontSize: 12,
                            fontFamily: "Inter Bold",
                            color: theme.palette.gray[600],
                            paddingX: 2,
                        },
                        ".MuiDataGrid-columnHeaderCheckbox": {
                            paddingLeft: 0.5,
                        },
                        ".MuiDataGrid-cell:not(.MuiDataGrid-cellCheckbox)": {
                            fontSize: 12,
                            paddingX: 2,
                        },
                        ".MuiCheckbox-root:not(.Mui-checked) .MuiSvgIcon-root":
                            {
                                color: theme.palette.gray[300],
                            },
                        ".MuiDataGrid-cellCheckbox .MuiCheckbox-root.Mui-disabled": {
                            opacity: 0.5,
                        },
                        ".MuiDataGrid-footerContainer": {
                            fontSize: 12,
                        },
                        ...(props.exportCapture
                            ? {
                                  // Every row is rendered, so a scrollbar would only add noise to the image.
                                  "& .MuiDataGrid-virtualScroller": {
                                      overflow: "hidden !important",
                                  },
                                  "& .MuiDataGrid-scrollbar, & .MuiDataGrid-scrollbarFiller":
                                      {
                                          display: "none",
                                      },
                              }
                            : {}),
                        ...(props.disableColumnResize
                            ? {
                                  "& .MuiDataGrid-columnSeparator": {
                                      display: "none",
                                  },
                              }
                            : {}),
                        ...(store.rowHeightOverride
                            ? {
                                  "& .MuiDataGrid-cell": {
                                      display: "flex",
                                      alignItems: "center",
                                      paddingY: 0.5,
                                  },
                              }
                            : {}),
                    }}
                />
            </div>
            {gridApi?.current && <HideColumn store={store} />}
        </>
    );
});

export default AcxDataGrid;
